import { Timestamp } from '@firebase/firestore-types';
import { Telemetry } from '@just-in-types';
import * as NoSleep from 'nosleep.js';
import store from '..';
import { firestore } from '../../firebase';
import { unregister } from '../../serviceWorker';

export const GET_LOCAL_IPS = 'telemetry.GET_LOCAL_IPS';
export const getLocalIps = (): Promise<string[]> =>
  new Promise((resolve, reject) => {
    // Older browsers don't have access to RTCPeerConnection
    if (!RTCPeerConnection) {
      resolve([]);
    }
    const rtc = new RTCPeerConnection({ iceServers: [] });
    const addresses: { [ip: string]: boolean } = {};

    addresses['0.0.0.0'] = false;

    const updateDisplay = (newAddr: string) => {
      if (newAddr in addresses) {
        return;
      } else {
        addresses[newAddr] = true;
      }
      const displayAddrs = Object.keys(addresses).filter(k => addresses[k]);

      resolve(displayAddrs);
    };
    const grepSDP = (sdp?: string) => {
      if (sdp) {
        sdp.split('\r\n').forEach(line => {
          if (line.indexOf('a=candidate') !== -1) {
            const parts = line.split(' ');
            const addr = parts[4];
            const type = parts[7];

            if (type === 'host') {
              updateDisplay(addr);
            }
          } else if (line.indexOf('c=') !== -1) {
            const parts = line.split(' ');
            const addr = parts[2];

            updateDisplay(addr);
          }
        });
      }
    };
    rtc.createDataChannel('');

    rtc.onicecandidate = event => {
      // convert the candidate to SDP so we can run it through our general parser
      // see https://twitter.com/lancestout/status/525796175425720320 for details
      if (event.candidate) {
        grepSDP('a=' + event.candidate.candidate);
      }
    };

    rtc
      .createOffer()
      .then(offerDescription => {
        grepSDP(offerDescription.sdp);

        rtc.setLocalDescription(offerDescription);
      })
      .catch(error => {
        console.error(error);

        reject([]);
      });
  });

export const GET_PUBLIC_IP = 'telemetry.GET_PUBLIC_IP';
export const getPublicIp = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const json = (await response.json()) as { ip: string };

    return json.ip;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const GET_TABLET_PROPS = 'telemetry.GET_TABLET_PROPS';
export const getTabletProps = async () => {
  try {
    const doc = await firestore
      .collection('admin')
      .doc('globalTabletProps')
      .get();
    const data = doc.data();

    const { alwaysReload } = data as { alwaysReload: boolean };

    store.dispatch({ payload: { alwaysReload }, type: GET_TABLET_PROPS });
  } catch (error) {
    throw new Error(error);
  }
};

export const GET_CREDENTIALS = 'telemetry.GET_CREDENTIALS';
export const getCredentials = async () => {
  const publicIp = await getPublicIp();

  if (typeof publicIp === 'string') {
    const doc = await firestore
      .collection('ipAddresses')
      .doc(publicIp)
      .get();

    const credentials = doc.data() as IKioskCredentials;

    store.dispatch({ payload: credentials, type: GET_CREDENTIALS });

    return credentials;
  } else {
    return null;
  }
};

export const updateKioskCredentials = ({
  businessId,
  businessPath,
  siteId,
  sitePath,
}: IKioskCredentials) =>
  firestore.runTransaction(async transaction => {
    try {
      const [localIps, publicIp] = await Promise.all([
        getLocalIps(),
        getPublicIp(),
      ]);

      if (publicIp) {
        const ipRef = firestore.collection('ipAddresses').doc(publicIp);
        const doc = await transaction.get(ipRef);
        const credentialsData = doc.data() as IKioskCredentials;
        const newLocalIps = [...localIps];

        if (credentialsData && credentialsData.localIps) {
          credentialsData.localIps.forEach(
            ip => newLocalIps.indexOf(ip) === -1 && newLocalIps.push(ip),
          );
        }

        const credentials: IKioskCredentials = {
          businessId,
          businessPath,
          lastUpdated: new Date(),
          localIps: newLocalIps,
          publicIp,
          siteId,
          sitePath,
        };

        store.dispatch({ payload: credentials, type: GET_CREDENTIALS });

        return transaction.set(ipRef, credentials);
      }
    } catch (error) {
      console.error(error);
    }
  });

export interface IKioskCredentials {
  publicIp?: string;
  businessId: string;
  businessPath: string;
  lastUpdated?: Date | Timestamp;
  localIps?: string[];
  siteId: string;
  sitePath: string;
  version?: string;
}

const noSleep = new NoSleep();

export const ENABLE_NO_SLEEP = 'telemetry.ENABLE_NO_SLEEP';
export const enableNoSleep = () => {
  noSleep.enable();
  store.dispatch({ type: ENABLE_NO_SLEEP });
  document.removeEventListener('click', enableNoSleep, false);
};

document.addEventListener('click', enableNoSleep, false);

export const GET_APP_INFO = 'telemetry.GET_APP_INFO';
export const GET_APP_INFO_SUBSCRIPTION = 'telemetry.GET_APP_INFO_SUBSCRIPTION';
export const getAppInfo = async () => {
  const unsubscribe = await firestore
    .collection('telemetry')
    .doc('appInfo')
    .onSnapshot({ includeMetadataChanges: true }, doc => {
      const appInfo = doc.data() as Telemetry.AppInfo;

      store.dispatch({
        payload: appInfo,
        type: GET_APP_INFO,
      });
    });

  store.dispatch({ payload: unsubscribe, type: GET_APP_INFO_SUBSCRIPTION });
};

const deleteCache = async () => {
  try {
    const cacheKeys = await window.caches.keys();

    console.group('delete cache');
    if (cacheKeys.length > 0) {
      const [unregisterResponse, deleteResponses] = await Promise.all([
        unregister(),
        Promise.all(cacheKeys.map(cacheKey => window.caches.delete(cacheKey))),
      ]);

      console.log(unregisterResponse);

      if (deleteResponses.every(response => response)) {
        console.log('all caches deleted');
      } else {
        console.error('some caches were not deleted');
      }
    } else {
      console.log('no caches found');
    }
    console.groupEnd();
  } catch (error) {
    throw new Error(error);
  }
};

const deleteIndexedDbs = () => {
  const deleteDb = (dbName: string) => {
    const request = window.indexedDB.deleteDatabase(dbName);

    request.onerror = () => {
      console.log(`ERROR deleting ${dbName}`);
    };
    request.onsuccess = () => {
      console.log(`deleting ${dbName} was SUCCESSFUL!`);
    };
    request.onblocked = () => {
      console.log(`deleting ${dbName} was BLOCKED`);
    };
  };
  const dbNames = [
    'firestore/[DEFAULT]/just-in-north/main',
    'firebaseLocalStorageDb',
  ];

  console.group('delete indexedDb');
  if ('indexedDB' in window) {
    dbNames.forEach(dbName => deleteDb(dbName));
  }
  console.groupEnd();
};

export const adminReload = async (clearCache?: boolean) => {
  try {
    if (clearCache) {
      await deleteCache();

      deleteIndexedDbs();
    }

    window.location.reload();
  } catch (error) {
    throw new Error(error);
  }
};

// export interface IMemoryUsage {
//   memory: {
//     jsHeapSizeLimit: number;
//     totalJSHeapSize: number;
//     usedJSHeapSize: number;
//     usedJSRatio: number;
//   };
// }
// const memoryUsage: IMemoryUsage = (() => {
//   const memory: any = performance;
//   const { jsHeapSizeLimit, totalJSHeapSize, usedJSHeapSize } = memory;
//   const usedJSRatio = parseFloat((usedJSHeapSize / totalJSHeapSize).toFixed(2));

//   return {
//     memory: {
//       jsHeapSizeLimit,
//       totalJSHeapSize,
//       usedJSHeapSize,
//       usedJSRatio
//     }
//   };
// })();

// export type CatchError = (
//   error: Error,
//   errorInfo?: object
// ) => (dispatch: Dispatch<AnyAction>) => Raven.RavenStatic;
// export interface IErrorInfo {
//   componentStack: string;
// }
// export const CATCH_ERROR = 'telemetry.CATCH_ERROR';
// export const catchError = (error: Error, errorInfo?: IErrorInfo) => (
//   dispatch: Dispatch
// ) => {
//   dispatch({ payload: { memoryUsage, ...errorInfo }, type: CATCH_ERROR });

//   return Raven.captureException(error, {
//     extra: { memoryUsage, ...errorInfo }
//   });
// };



// WEBPACK FOOTER //
// ./src/store/actions/telemetryActions.ts